import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Edit01, Trash01 } from "@vctors/vctors-untitled-ui/icons";
import {
  ButtonUtility,
  LoadingIndicator,
  PaginationPageMinimalCenter,
  Table,
  TableCard,
} from "@vctors/vctors-untitled-ui";
import type { SortDescriptor } from "react-aria-components";
import { formatDateWithMoment } from "@surgia/utils";
import {
  handlePaginationChange,
  handleSort,
  parseSortDescriptor,
} from "../../utils/table";
import { GenericTableHeader } from "@surgia/ui";
import Badge from "@surgia/ui/components/badge";
import checkedIcon from "@surgia/ui/icons/checkbox/checked.svg";
import uncheckedIcon from "@surgia/ui/icons/checkbox/unchecked.svg";
import TruncatedTooltip from "@surgia/ui/components/truncatedTooltip";
interface IPurchaseOrder {
  id: number;
  createdBy: string;
  branch: string;
  totalQuantity: number;
  "orderSummary.total": number;
  createdAt: string;
  status: string;
  branchId: number;
  numberOfPurchaseRequests: number; // Add this line
}

interface IPurchaseOrderTableProps {
  data: IPurchaseOrder[];
  loading?: boolean;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  showBranchColumn?: boolean;
  selectedPurchaseOrders: string[];
  setSelectedPurchaseOrders: (
    orders: string[] | ((prev: string[]) => string[])
  ) => void;
  status: string | undefined;
  setStatus: (status: string) => void;
  onViewDetails: (poId: string, branchId: string) => void;
  onEdit?: (poId: string) => void;
  totalCount?: number;
  onDelete?: (poId: string) => void;
}

export function PurchaseOrderTableUntitled({
  data,
  loading,
  currentPage = 0,
  totalPages = 1,
  onPageChange,
  showBranchColumn = true,
  selectedPurchaseOrders,
  setSelectedPurchaseOrders,
  status,
  onViewDetails,
  onEdit,
  onDelete,
  totalCount,
}: IPurchaseOrderTableProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get sort parameter from URL
  const sortParam = searchParams.get("sort");
  const pageParam = searchParams.get("page");

  // sortDescriptor is used by the Table component for UI visual indicators
  // Update the initial sortDescriptor to not have any column sorted
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>(
    parseSortDescriptor(sortParam || undefined, "id", "descending")
  );

  // Sync sortDescriptor with URL changes to keep UI indicators accurate
  useEffect(() => {
    setSortDescriptor(
      parseSortDescriptor(sortParam || undefined, "id", "descending")
    );
  }, [sortParam]);

  const onSortChange = (descriptor: SortDescriptor) => {
    setSortDescriptor(descriptor);
    handleSort(
      navigate,
      descriptor.column as string,
      descriptor.direction as string
    );
  };

  const onPageChangeHandler = (page: number) => {
    if (onPageChange) {
      onPageChange(page - 1);
    } else {
      handlePaginationChange(navigate, page - 1);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedPurchaseOrders(data.map((item) => item.id.toString()));
    } else {
      setSelectedPurchaseOrders([]);
    }
  };

  const isAllSelected =
    data?.length > 0 && selectedPurchaseOrders?.length === data?.length;
  const isIndeterminate =
    selectedPurchaseOrders?.length > 0 &&
    selectedPurchaseOrders?.length < data?.length;

  // Calculate total columns for colSpan
  const hasActionsColumn = !!(onEdit || onDelete);
  const totalColumns =
    7 + (showBranchColumn ? 1 : 0) + (hasActionsColumn ? 1 : 0);

  const getStatusBadgeText = (status: string) => {
    switch (status) {
      case "PENDING":
        return `${totalCount} Pending`;
      case "APPROVED":
        return `${totalCount} Approved`;
      case "COMPLETED":
        return `${totalCount} Completed`;
      default:
        return `${totalCount} orders`;
    }
  };

  return (
    <TableCard.Root className="bg-white !ring-[#E9EAEB] !ring-1 !shadow-none !border-none">
      <TableCard.Header
        className="bg-white"
        title=""
        badge=""
        contentTrailing={
          <GenericTableHeader
            title="Purchase Orders"
            totalCount={totalCount || 0}
            showBadge={true}
            customBadgeText={getStatusBadgeText(status || "PENDING")}
            showBorder={false}
            padding=""
            layout="justified"
            className="w-full"
          >
            <div className="flex items-center gap-2">
              {/* <div className="relative">
                <input
                  type="text"
                  placeholder="Search"
                  className="w-64 px-3 py-2 pl-10 border border-gray-200 rounded-lg focus:outline-none "
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-4 w-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div> */}
              {/* <Button color="secondary" data-testid="filters-button">
                {t("filters", "Filters")}
              </Button> */}
            </div>
          </GenericTableHeader>
        }
      />

      {/* Selection Controls */}
      {status === "PENDING" && (
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <img
              src={
                isAllSelected
                  ? checkedIcon
                  : isIndeterminate
                    ? checkedIcon
                    : uncheckedIcon
              }
              alt="select all"
              onClick={() => handleSelectAll(!isAllSelected)}
              className="cursor-pointer w-4 h-4"
            />
            <span className="text-sm text-gray-600">
              {selectedPurchaseOrders?.length} of {data?.length} selected
            </span>
          </div>
        </div>
      )}

      <Table
        aria-label="Purchase Orders"
        selectionMode="none"
        sortDescriptor={sortDescriptor}
        onSortChange={onSortChange}
        className="bg-white"
      >
        <Table.Header className="bg-[#FAFAFA] text-[#717680]">
          <Table.Head id="id" allowsSorting label="ID" isRowHeader></Table.Head>
          <Table.Head id="createdBy" label="Ordered By" allowsSorting />
          <Table.Head
            id="numberOfPurchaseRequests"
            label="NO.PRS"
            allowsSorting
          />
          <Table.Head id="totalQuantity" label="Qty" allowsSorting />
          <Table.Head id="orderTotal" label="Total price (RS)" allowsSorting />
          {showBranchColumn && (
            <Table.Head id="branch" label="Shipping To" allowsSorting />
          )}
          <Table.Head id="createdAt" label="Date created" allowsSorting />
          <Table.Head id="status" label="Status" allowsSorting />
          {(onEdit || onDelete) && <Table.Head id="actions" />}
        </Table.Header>

        <Table.Body>
          {loading ? (
            <Table.Row className="border-t border-[#E9EAEB]">
              <Table.Cell colSpan={totalColumns}>
                <div className="flex h-64 items-center justify-center">
                  <LoadingIndicator type="dot-circle" size="md" />
                </div>
              </Table.Cell>
            </Table.Row>
          ) : data?.length > 0 ? (
            data.map((item: IPurchaseOrder) => (
              <Table.Row
                key={item.id}
                id={item.id.toString()}
                className="border-t border-[#E9EAEB]"
              >
                <Table.Cell>
                  <div className="relative group">
                    <button
                      onClick={() =>
                        onViewDetails(
                          item.id.toString(),
                          item.branchId.toString()
                        )
                      }
                      className="cursor-pointer text-sm  text-purple-700 hover:text-purple-700/80 transition-colors"
                    >
                      {item.id}
                    </button>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
                      {`View ${item.id}`}
                    </div>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className="flex items-center gap-3">
                    <TruncatedTooltip
                      content={item.createdBy}
                      textClassName="max-w-[150px] text-sm font-medium text-primary break-words"
                      as="p"
                    />
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-gray-600">
                    {item.totalQuantity || 0}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-gray-600">
                    {item.numberOfPurchaseRequests}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span className="text-sm text-gray-600">
                    {item["orderSummary.total"]
                      ? `${item["orderSummary.total"]}`
                      : "-"}
                  </span>
                </Table.Cell>
                {showBranchColumn && (
                  <Table.Cell>
                    <div className="relative group">
                      <Badge
                        variant="default"
                        className="truncate max-w-[150px]"
                      >
                        {item.branch}
                      </Badge>
                      <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-sm rounded opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50 min-w-max">
                        {item.branch}
                      </div>
                    </div>
                  </Table.Cell>
                )}
                <Table.Cell>
                  <span className="text-sm text-gray-600">
                    {item.createdAt
                      ? formatDateWithMoment(item.createdAt)
                      : "-"}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <Badge
                    variant={"default"}
                    // item.status.startsWith("O")
                    //   ? ""
                    //   : item.status.startsWith("P")
                    //     ? "OTHER"
                    //     : item.status.startsWith("A")
                    //       ? "clinical"
                    //       : item.status.startsWith("C")
                    //         ? "procurement"
                    //         : "default"
                  >
                    {item.status.charAt(0).toUpperCase() +
                      item.status.slice(1).toLowerCase()}
                  </Badge>
                </Table.Cell>
                {(onEdit || onDelete) && (
                  <Table.Cell className="px-4">
                    <div className="flex justify-end gap-1">
                      {onEdit && (
                        <ButtonUtility
                          size="xs"
                          color="tertiary"
                          tooltip="Edit"
                          icon={Edit01}
                          onClick={() => onEdit(item.id.toString())}
                          data-testid="edit-po-button"
                        />
                      )}

                      {onDelete && (
                        <ButtonUtility
                          size="xs"
                          color="tertiary"
                          tooltip="Delete"
                          icon={Trash01}
                          onClick={() => onDelete(item.id.toString())}
                          data-testid="delete-po-button"
                        />
                      )}
                    </div>
                  </Table.Cell>
                )}
              </Table.Row>
            ))
          ) : (
            <Table.Row className="border-t border-[#E9EAEB]">
              <Table.Cell colSpan={totalColumns}>
                <div className="flex h-64 items-center justify-center">
                  <p className="text-gray-500 text-center">No Data found</p>
                </div>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>

      <div className="[&_*]:!border-gray-200 [&_*]:!ring-gray-200 [&_button]:!bg-white [&_button]:!text-gray-700 [&_button[aria-current='true']]:!bg-gray-200 [&_button[aria-current='true']]:!text-gray-900">
        <PaginationPageMinimalCenter
          page={(parseInt(pageParam || "0") || currentPage) + 1}
          total={totalPages}
          onPageChange={onPageChangeHandler}
          className="px-4 py-3 md:px-6 md:pt-3 md:pb-4"
        />
      </div>
    </TableCard.Root>
  );
}
